"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useCurrentUser } from "@/hooks/useApi"
import type { User } from "@/lib/types"
import { loadAuthToken, loadCompanyId, loadSelectedAppId, logout, setAuthToken, setCompanyId } from "@/lib/api"

// Public paths that don't require authentication
const publicPaths = ["/login", "/register", "/forgot-password", "/reset-password", "/password-reset"]

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  logout: () => { },
  refreshUser: async () => { },
})

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  
  // Load auth token, company ID, and selected app ID immediately (synchronously)
  // This ensures they're available before any API calls
  useState(() => {
    if (typeof window !== 'undefined') {
      loadAuthToken()
      loadCompanyId()
      loadSelectedAppId()
    }
    return null
  })
  
  const { data: user, loading: isLoading, error, refetch } = useCurrentUser()
  // Initialize isAuthenticated based on whether we have a token
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (typeof window !== 'undefined') {
      return !!localStorage.getItem('auth_token')
    }
    return false
  })

  // Handle authentication state changes
  useEffect(() => {
    if (isLoading) return

    const hasToken = localStorage.getItem('auth_token')

    if (user) {
      setIsAuthenticated(true)
    } else if (!hasToken) {
      // No token at all - not authenticated
      setIsAuthenticated(false)
      
      // Redirect to login if not on a public path
      const isPublicPath = publicPaths.some((path) => pathname === path || pathname.startsWith(`${path}/`))
      if (!isPublicPath && pathname !== "/login") {
        navigate(`/login?redirect=${encodeURIComponent(pathname)}`)
      }
    } else if (error && error.startsWith('401:')) {
      // We have a token but got a 401 error - token is invalid
      setIsAuthenticated(false)
      
      // Clear all auth data
      localStorage.removeItem('user_data')
      localStorage.removeItem('current_company')
      localStorage.removeItem('user_companies')
      localStorage.removeItem('auth_token')
      localStorage.removeItem('company_id')
      
      // Also clear API client tokens
      setAuthToken(null)
      setCompanyId(null)
      
      // Redirect to login if not on a public path
      const isPublicPath = publicPaths.some((path) => pathname === path || pathname.startsWith(`${path}/`))
      if (!isPublicPath && pathname !== "/login") {
        navigate(`/login?redirect=${encodeURIComponent(pathname)}`)
      }
    }
    // If we have a token but no user yet and no 401 error, keep isAuthenticated as true
    // This handles the case where the API call is still pending or had a transient error
  }, [user, isLoading, error, navigate, pathname])

  // Logout function
  const handleLogout = async () => {
    try {
      // Before logging out, save the current company selection for next login
      const currentCompanyId = localStorage.getItem('company_id')
      if (currentCompanyId) {
        localStorage.setItem('last_selected_company_id', currentCompanyId)
      }
      
      await logout()
      
      // Clear all stored auth data (but keep last_selected_company_id)
      localStorage.removeItem('user_data')
      localStorage.removeItem('current_company')
      localStorage.removeItem('user_companies')
      localStorage.removeItem('auth_token')
      localStorage.removeItem('company_id')
      
      // Force a page reload to completely reset auth state
      window.location.href = '/login'
    } catch (error) {
      console.error("Logout failed:", error)
      // Even if API call fails, clear local session
      
      // Save the current company selection before clearing
      const currentCompanyId = localStorage.getItem('company_id')
      if (currentCompanyId) {
        localStorage.setItem('last_selected_company_id', currentCompanyId)
      }
      
      localStorage.removeItem('user_data')
      localStorage.removeItem('current_company')
      localStorage.removeItem('user_companies')
      localStorage.removeItem('auth_token')
      localStorage.removeItem('company_id')
      
      window.location.href = '/login'
    }
  }

  return <AuthContext.Provider value={{ user: user ?? null, isLoading, isAuthenticated, logout: handleLogout, refreshUser: refetch }}>{children}</AuthContext.Provider>
}

export function useAuth() {
  return useContext(AuthContext)
}
